import React, { useState, useEffect } from 'react';
import { Container, Card, Alert, Form, Spinner, ListGroup, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { League, Castaway } from '../types';

const AdminEliminationPicks: React.FC = () => {
  const [leagues, setLeagues] = useState<League[]>([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [selectedLeague, setSelectedLeague] = useState<League | null>(null);
  const [eliminationPicks, setEliminationPicks] = useState<any>({});
  const [castaways, setCastaways] = useState<Castaway[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<number>(() => {
    const saved = localStorage.getItem('adminSelectedSeason');
    return saved ? parseInt(saved) : 49;
  });

  const fetchLeagues = async (season: number) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`);
      const data = await response.json();
      const filteredLeagues = (data.leagues || []).filter((l: any) => (l.season || 49) === season);
      setLeagues(filteredLeagues);
      if (filteredLeagues.length > 0) {
          handleLeagueSelect(filteredLeagues[0].id, filteredLeagues);
      } else {
          setSelectedLeagueId('');
          setSelectedLeague(null);
          setEliminationPicks({});
          setCastaways([]);
      }
    } catch (err) {
      setError('Failed to fetch leagues.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeagues(selectedSeason);

    const handleSeasonChange = () => {
      const newSeason = parseInt(localStorage.getItem('adminSelectedSeason') || '49');
      setSelectedSeason(newSeason);
      fetchLeagues(newSeason);
    };
    window.addEventListener('adminSeasonChanged', handleSeasonChange);
    return () => window.removeEventListener('adminSeasonChanged', handleSeasonChange);
  }, []);

  const handleLeagueSelect = async (leagueId: string, leaguesList: League[] = leagues) => {
    const league = leaguesList.find(l => l.id === leagueId);
    if (!league) return;
    setSelectedLeagueId(leagueId);
    setSelectedLeague(league);
    setIsLoading(true);
    try {
      const [picksResponse, castawaysResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${leagueId}/elimination-picks`),
          fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${leagueId}/castaways`)
      ]);
      const picksData = await picksResponse.json();
      const castawaysData = await castawaysResponse.json();
      
      setEliminationPicks(picksData.eliminationPicks || {});
      setCastaways(castawaysData.castaways || []);
    } catch (err) {
      setError('Failed to fetch league data.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePickChange = async (userName: string, episode: string, castawayId: string) => {
    if (!selectedLeagueId) return;
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/leagues/${selectedLeagueId}/elimination-pick`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userName, episode, castawayId: parseInt(castawayId) }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      setSuccess('Pick updated successfully!');
      // Refresh picks
      const picksResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/elimination-picks`);
      const picksData = await picksResponse.json();
      setEliminationPicks(picksData.eliminationPicks || {});
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="mt-5">
      <div className="mb-3">
        <Link to="/admin/dashboard" className="text-decoration-none">
          &larr; Back to Admin Home
        </Link>
      </div>
      <Card shadow-sm home-card>
        <Card.Header as="h4" className="card-title-custom text-center">Admin - Edit Elimination Picks (Season {selectedSeason})</Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          
          {leagues.length > 1 && (
            <Form.Group className="mb-3">
                <Form.Label>Select League</Form.Label>
                <Form.Select value={selectedLeagueId} onChange={(e) => handleLeagueSelect(e.target.value)} disabled={isLoading}>
                {leagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </Form.Select>
            </Form.Group>
          )}

          {leagues.length === 1 && (
            <div className="mb-3 p-2 bg-light border rounded">
              <strong>League:</strong> {leagues[0].name}
            </div>
          )}

          {leagues.length === 0 && !isLoading && (
             <Alert variant="info">No leagues found for Season {selectedSeason}.</Alert>
          )}

          {isLoading && <div className="text-center my-3"><Spinner animation="border" /></div>}
          
          {selectedLeague && !isLoading && (
            <ListGroup>
              {selectedLeague.members.map((member: any) => (
                <ListGroup.Item key={member.userName}>
                  <strong>{member.userName}</strong>
                  {Object.keys(eliminationPicks[member.userName] || {}).map(episode => (
                    <Row key={episode} className="mt-2">
                      <Col md={2}>Episode {episode}</Col>
                      <Col md={4}>
                        <Form.Select
                          value={eliminationPicks[member.userName]?.[episode] || ''}
                          onChange={(e) => handlePickChange(member.userName, episode, e.target.value)}
                        >
                          <option>-- Select Castaway --</option>
                          {castaways.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </Form.Select>
                      </Col>
                    </Row>
                  ))}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AdminEliminationPicks;
